import { useQuery } from "@tanstack/react-query";

import { api } from "../api/rest";
import type {
  ConsensusBin,
  ConsensusCell,
  ConsensusSignal,
  ConsensusTakerBucket,
  ConsensusTakerReport,
} from "../api/types";

/**
 * Такт опроса СВОДКИ котировки группы.
 *
 * Пятнадцать секунд: сводка отвечает на вопросы замера — кромка, калибровка,
 * цена преследования, — и ни один из них не меняется быстрее.
 */
const CONSENSUS_REFETCH_MS = 15_000;

/**
 * Такт опроса ЛЕНТЫ — чаще сводки, и с вехи живого режима это не роскошь.
 *
 * Прежнее обоснование общего такта («кнопок у раздела нет, секунда задержки
 * ничего не стоит») перестало быть правдой: в ступени `alert` каждая строка
 * ленты несёт кнопку «взять», и жить ей ровно до конца окна. Строка рождается
 * ближе к концу пятиминутки — то есть пятнадцатисекундный такт съедал бы
 * заметную долю всего времени на решение, а иногда и всё.
 *
 * Три секунды, а не одна, и то же число, что у почти решённого: две привычки
 * руки на одном экране хуже одной, а выигрыш от ускорения дальше упирается в
 * длину окна, не в такт.
 */
const CONSENSUS_FEED_REFETCH_MS = 3_000;

/** Сводка котировки по клеткам сетки и минутам окна, с калибровкой корзинами. */
export function useConsensusSummary(hours: number, windowLen = 0) {
  return useQuery<{
    hours: number;
    cells: ConsensusCell[];
    nested: boolean;
    calibration: ConsensusBin[];
    taker_by_ask: ConsensusTakerBucket[];
  }>({
    queryKey: ["consensus-summary", hours, windowLen],
    queryFn: () => api.consensusSummary(hours, windowLen),
    refetchInterval: CONSENSUS_REFETCH_MS,
    retry: 1,
  });
}

/** Отчёт тейкера одной клетки воронкой; пустой code — главная клетка. */
export function useConsensusTaker(hours: number, cell: string) {
  return useQuery<ConsensusTakerReport>({
    queryKey: ["consensus-taker", hours, cell],
    queryFn: () => api.consensusTaker(hours, cell),
    refetchInterval: CONSENSUS_REFETCH_MS,
    retry: 1,
  });
}

/**
 * Лента ставок, свежие сверху. В ступени `alert` в ней живут кнопки.
 *
 * Погашенные правилом строки НЕ прячутся: они и есть ответ на вопрос «сколько мы
 * пропустили и почему», без которого пороги калибруются вслепую. Кнопки на них
 * при этом не появляются — правило судит вход, и трейдер отверг бы нажатие тем
 * же отказом.
 */
export function useConsensusSignals(hours: number, limit = 60) {
  return useQuery<{ signals: ConsensusSignal[] }>({
    queryKey: ["consensus-signals", hours, limit],
    queryFn: () => api.consensusSignals(hours, limit),
    refetchInterval: CONSENSUS_FEED_REFETCH_MS,
    // В фоновой вкладке не опрашиваем: нажимать по строке всё равно некому, а
    // такт в три секунды на скрытой вкладке — это запрос каждые три секунды на
    // всю ночь.
    refetchIntervalInBackground: false,
    retry: 1,
  });
}
